import React, { useState } from 'react';
import { useJobContacts } from '../../../hooks/useJobContacts';
import type { Contact } from '../../../types/contact';
import type { ContactRole } from '../../../types/job';

interface JobContactFormProps {
  jobId: string;
  contact: Contact;
  onSuccess: () => void;
  onCancel: () => void;
}

const ROLE_OPTIONS: { value: ContactRole; label: string }[] = [
  { value: 'interviewer', label: 'Interviewer' },
  { value: 'hiring_manager', label: 'Hiring Manager' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'referral', label: 'Referral' }
];

export default function JobContactForm({ jobId, contact, onSuccess, onCancel }: JobContactFormProps) {
  const { addContact } = useJobContacts(jobId);
  const [role, setRole] = useState<ContactRole>('interviewer');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const { error: saveError } = await addContact(contact.id, role, notes);
    if (saveError) {
      setError(saveError);
      setSaving(false);
      return;
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="font-medium">{contact.first_name} {contact.last_name}</p>
        {contact.job_title && (
          <p className="text-sm text-gray-400">{contact.job_title}</p>
        )}
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-1">
          Role <span className="text-red-500">*</span>
        </label>
        <select
          id="role"
          value={role}
          onChange={(e) => setRole(e.target.value as ContactRole)}
          className="input w-full"
          required
        >
          {ROLE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="input w-full"
          rows={3}
          placeholder="Add any relevant notes about this contact's involvement..."
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Adding...' : 'Add Contact'}
        </button>
      </div>
    </form>
  );
}